<script setup lang="ts">
import { computed, watch } from 'vue'

import type {
  ChartFieldCandidate,
  ChartFieldRoleSelection,
  ChartFieldRoleSlot,
  ChartFieldRoleSources,
} from '../../models/chart.types'
import { applyFieldRoleSelectionRules } from '../../service/chart/fieldRoles'
import ReportToolbarSelect from '../controls/ReportToolbarSelect.vue'

import { Icon, MultiSelect, Select, Tooltip, type IconName } from '@/shared/ui'

const model = defineModel<ChartFieldRoleSelection>({ default: () => ({}) })
const props = withDefaults(
  defineProps<{
    roles: readonly import('../../models/chart.types').ChartFieldRole[]
    sources: ChartFieldRoleSources
    disabled?: boolean
    compact?: boolean
    /** Lay the slots out as an even grid of full-width fields (a form), not an inline strip. */
    fill?: boolean
    clearLabel?: string
  }>(),
  { disabled: false, compact: false, fill: false },
)
const emit = defineEmits<{ 'validity-change': [valid: boolean] }>()
// The clear label is host-owned copy: an omitted prop falls through to `ReportToolbarSelect`'s own
// translated fallback rather than an English default declared here.
function clearLabelBinding(label: string): { clearLabel?: string } {
  return props.clearLabel === undefined ? {} : { clearLabel: `${props.clearLabel} ${label}` }
}

const resolution = computed(() =>
  applyFieldRoleSelectionRules(props.roles, props.sources, model.value),
)
const renderedSlots = computed(() =>
  props.compact
    ? resolution.value.slots.filter(
        (slot) => slot.role.required || slot.options.length > 0 || slot.value.length > 0,
      )
    : resolution.value.slots,
)

function sameSelection(left: ChartFieldRoleSelection, right: ChartFieldRoleSelection): boolean {
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort()
  return keys.every((key) => {
    const leftValue = left[key] ?? []
    const rightValue = right[key] ?? []
    return (
      leftValue.length === rightValue.length &&
      leftValue.every((value, i) => value === rightValue[i])
    )
  })
}

function updateRole(key: string, value: string | string[] | null): void {
  const next = {
    ...model.value,
    [key]: Array.isArray(value) ? value : value === null ? [] : [value],
  }
  model.value = applyFieldRoleSelectionRules(props.roles, props.sources, next).selection
}

/** A field cannot occupy two semantic slots of the same backend field type. Keep the current
 * slot's value selectable, and disable values already owned by sibling slots. */
function optionsFor(slot: ChartFieldRoleSlot): ChartFieldCandidate[] {
  const usedBySibling = new Set(
    resolution.value.slots
      .filter(
        (candidate) =>
          candidate.role.key !== slot.role.key && candidate.role.fieldType === slot.role.fieldType,
      )
      .flatMap((candidate) => candidate.value),
  )
  const current = new Set(slot.value)
  return slot.options.map((option) => ({
    ...option,
    disabled:
      option.disabled === true || (usedBySibling.has(option.value) && !current.has(option.value)),
  }))
}

function roleIcon(slot: ChartFieldRoleSlot): IconName {
  if (slot.role.fieldType === 'time_dimension') return 'clock'
  if (slot.role.fieldType === 'dimension') return 'circle-nodes'
  return 'chart-line-up'
}

function roleSelectionLabel(slot: ChartFieldRoleSlot): string {
  const labels = slot.value.map(
    (value) => slot.options.find((option) => option.value === value)?.label ?? value,
  )
  return labels.length ? labels.join(', ') : slot.role.label
}

watch(
  () => resolution.value.selection,
  (selection) => {
    if (!sameSelection(model.value, selection)) model.value = selection
  },
  { immediate: true, deep: true },
)
watch(
  () => resolution.value.valid,
  (valid) => emit('validity-change', valid),
  { immediate: true },
)
</script>

<template>
  <div
    class="chart-field-roles items-start gap-sm"
    :class="[
      fill
        ? 'grid w-full grid-cols-4 gap-md max-md:grid-cols-2 max-sm:grid-cols-1'
        : 'flex w-max overflow-x-auto',
      { 'chart-field-roles--compact': compact },
    ]"
    :data-valid="resolution.valid"
  >
    <div
      v-for="slot in renderedSlots"
      :key="slot.role.key"
      class="chart-field-roles__slot grid gap-2xs"
      :class="fill ? 'w-full min-w-0' : 'w-max max-w-34'"
    >
      <Tooltip :text="slot.role.description ?? ''">
        <div
          v-if="compact && slot.role.multiple"
          class="chart-field-roles__compact-multi relative flex max-w-34 flex-1 h-11 items-center gap-1.5 p-xs rounded-md text-primary-muted [&:hover]:bg-surface-sunken [&:hover]:text-primary [&:focus-within]:bg-surface-sunken [&:focus-within]:text-primary [&:focus-within]:shadow-[0_0_0_2px_var(--ui-color-ring)]"
        >
          <Icon :name="roleIcon(slot)" :size="16" aria-hidden="true" />
          <span
            class="chart-field-roles__compact-copy flex min-w-0 flex-1 flex-col items-start leading-none pointer-events-none"
          >
            <strong>{{ slot.role.label }}</strong>
            <small :title="roleSelectionLabel(slot)">{{ roleSelectionLabel(slot) }}</small>
            <MultiSelect
              :model-value="slot.value"
              :options="optionsFor(slot)"
              :label="slot.role.label"
              :placeholder="slot.role.label"
              :max="slot.role.max"
              :display-chips="false"
              :disabled="disabled"
              variant="picker"
              show-select-all
              searchable
              size="sm"
              @update:model-value="updateRole(slot.role.key, $event)"
            />
          </span>
          <Icon
            class="chart-field-roles__compact-chevron relative z-2 flex-none text-muted"
            name="chevron-down"
            :size="10"
            aria-hidden="true"
          />
        </div>
        <ReportToolbarSelect
          v-else-if="compact"
          :model-value="slot.value[0] ?? null"
          :options="optionsFor(slot)"
          :label="slot.role.label"
          :placeholder="slot.role.label"
          :icon="roleIcon(slot)"
          :searchable="true"
          :disabled="disabled"
          :clearable="!slot.role.required"
          v-bind="clearLabelBinding(slot.role.label)"
          @update:model-value="updateRole(slot.role.key, $event)"
        />
        <MultiSelect
          v-else-if="slot.role.multiple"
          :model-value="slot.value"
          :options="optionsFor(slot)"
          :label="slot.role.label"
          :placeholder="slot.role.label"
          :max="slot.role.max"
          :display-chips="false"
          :disabled="disabled"
          :variant="fill ? 'default' : 'picker'"
          show-select-all
          searchable
          :size="fill ? 'lg' : 'sm'"
          @update:model-value="updateRole(slot.role.key, $event)"
        />
        <Select
          v-else
          :model-value="slot.value[0] ?? null"
          :options="optionsFor(slot)"
          :label="slot.role.label"
          :placeholder="slot.role.label"
          :clearable="!slot.role.required"
          :disabled="disabled"
          searchable
          :size="fill ? 'md' : 'sm'"
          @update:model-value="updateRole(slot.role.key, $event)"
        />
      </Tooltip>
    </div>
  </div>
</template>

<style scoped>
.chart-field-roles.chart-field-roles--compact {
  align-items: center;
}
.chart-field-roles .chart-field-roles__compact-copy > strong {
  position: relative;
  z-index: 2;
  max-width: 100%;
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chart-field-roles .chart-field-roles__compact-copy > small {
  position: relative;
  z-index: 2;
  max-width: 100%;
  overflow: hidden;
  color: var(--ui-color-muted);
  font-size: 0.625rem;
  font-weight: 400;
  line-height: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms) {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__label) {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__control) {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  flex-wrap: nowrap;
  border-style: solid;
  border-width: 0px;
  border-radius: 0;
  background-color: transparent;
  box-shadow: none;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__control:focus-within) {
  border-color: transparent;
  box-shadow: none;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__selection-summary) {
  display: none;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__field) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  cursor: pointer;
  caret-color: transparent;
  color: transparent;
}
.chart-field-roles .chart-field-roles__compact-copy :deep(.ui-ms__chevron) {
  display: none;
}
.chart-field-roles .chart-field-roles__compact-multi > :deep(.ui-icon) {
  position: relative;
  z-index: 2;
  pointer-events: none;
}

.chart-field-roles__slot :deep(.ui-tooltip-trigger) {
  width: 100%;
}
.chart-field-roles__slot:has(.chart-field-roles__compact-multi) {
  gap: 0;
}
.chart-field-roles__slot:has(.report-toolbar-select) {
  min-width: 6.5rem;
  gap: 0;
}
</style>
